import React, { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip } from 'recharts';
import { currencySymbol } from '../../utils/formatters';

const resolveVar = (varName) => {
  try {
    if (typeof window === 'undefined' || !window.document) return `var(${varName})`;
    const val = getComputedStyle(document.documentElement).getPropertyValue(varName);
    return (val && val.trim()) ? val.trim() : `var(${varName})`;
  } catch (e) {
    return `var(${varName})`;
  }
};


const formatValue = (value) => {
  const v = Number(value || 0);
  const sym = currencySymbol();
  if (v === 0) return `${sym}0`;
  if (v >= 1_000_000) return `${sym}${(v / 1_000_000).toFixed(1)}M`;
  return `${sym}${(v / 1_000).toFixed(0)}K`;
};

const ChartTooltipContent = ({ active, payload, labels = {} }) => {
  if (!active || !payload || !payload.length) return null;
  const p = payload;
  return (
    <div className="rounded-md border border-succession-border bg-succession-bg-primary p-2 text-xs shadow-sm">
      {p.map((it) => (
        <div key={it.name} className="flex items-center gap-2">
          <span className="inline-block w-2 h-2 rounded-sm" style={{ background: it.color }} />
          <span className="text-succession-text-secondary">{labels[it.name] || it.name}:</span>
          <span className="font-medium text-succession-text-primary">{formatValue(it.value)}</span>
        </div>
      ))}
    </div>
  );
};

export default function MORLineChart({ data = [], className = '' }) {
  const intl = useIntl();
  const [activeIndex, setActiveIndex] = useState(null);

  const seriesLabels = {
    Low: intl.formatMessage({ id: 'succession.multi_line_valuation.table_low' }),
    Mid: intl.formatMessage({ id: 'succession.multi_line_valuation.table_mid' }),
    High: intl.formatMessage({ id: 'succession.multi_line_valuation.table_high' }),
    Weighted: intl.formatMessage({ id: 'succession.multi_line_valuation.table_weighted' }),
  };

  const colors = useMemo(() => {
    const fallback = {
      Low: 'rgb(239,68,68)',
      Mid: 'rgb(234,179,8)',
      High: 'rgb(16,185,129)',
      Weighted: 'rgb(59,130,246)',
    };
    const c = {
      Low: resolveVar('--succession-error'),
      Mid: resolveVar('--succession-warning'),
      High: resolveVar('--succession-success'),
      Weighted: resolveVar('--succession-secondary'),
    };
    return {
      Low: c.Low && c.Low !== `var(--succession-error)` ? c.Low : fallback.Low,
      Mid: c.Mid && c.Mid !== `var(--succession-warning)` ? c.Mid : fallback.Mid,
      High: c.High && c.High !== `var(--succession-success)` ? c.High : fallback.High,
      Weighted: c.Weighted && c.Weighted !== `var(--succession-secondary)` ? c.Weighted : fallback.Weighted,
    };
  }, []);

  // Format line label for display
  const formatLineLabel = (lineId) => {
    if (!lineId) return '';
    const id = String(lineId).toLowerCase();
    if (id === 'pc') return 'P&C';
    return id.charAt(0).toUpperCase() + id.slice(1);
  };

  const chartData = (data || []).map((line) => ({
    name: formatLineLabel(line.line),
    Low: Number(line.low || 0),
    Mid: Number(line.mid || 0),
    High: Number(line.high || 0),
    Weighted: Number(line.weighted || 0),
  }));

  // Determine which series actually have data (handle missing lines/series)
  const presentSeries = useMemo(() => {
    const keys = ['Low', 'Mid', 'High', 'Weighted'];
    return keys.filter((k) => chartData.some((row) => Number(row[k] || 0) > 0));
  }, [chartData]);

  const yMax = useMemo(() => {
    const vals = [];
    chartData.forEach((r) => { presentSeries.forEach((k) => vals.push(Number(r[k] || 0))); });
    const m = Math.max(0, ...vals);
    return m > 0 ? m * 1.15 : 1; // expand a bit for headroom
  }, [chartData, presentSeries]);

  return (
    <div className={`w-full ${className}`}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }} className="bg-succession-bg-primary border border-succession-border rounded-lg p-6">
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-succession-text-primary mb-2">{intl.formatMessage({ id: 'succession.multi_line_valuation.mor_chart_title' })}</h3>
          <p className="text-sm text-succession-text-secondary">{intl.formatMessage({ id: 'succession.multi_line_valuation.mor_chart_subtitle' })}</p>
        </div>

        <div style={{ height: 400, width: '100%' }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 60 }} barGap={8} barCategoryGap="30%" maxBarSize={48}>
              <CartesianGrid strokeDasharray="3 3" stroke={resolveVar('--succession-border')} opacity={0.3} />
              <XAxis dataKey="name" stroke={resolveVar('--succession-text-secondary')} fontSize={12} tickLine={false} axisLine={false} />
              <YAxis stroke={resolveVar('--succession-text-secondary')} fontSize={12} tickLine={false} axisLine={false} tickFormatter={formatValue} domain={[0, yMax]} />
              <Tooltip content={<ChartTooltipContent labels={seriesLabels} />} />
              <Bar isAnimationActive={false} minPointSize={2} dataKey="Low" fill={colors.Low} radius={[4, 4, 0, 0]} onMouseEnter={(_, i) => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} hide={!presentSeries.includes('Low')} />
              <Bar isAnimationActive={false} minPointSize={2} dataKey="Mid" fill={colors.Mid} radius={[4, 4, 0, 0]} onMouseEnter={(_, i) => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} hide={!presentSeries.includes('Mid')} />
              <Bar isAnimationActive={false} minPointSize={2} dataKey="High" fill={colors.High} radius={[4, 4, 0, 0]} onMouseEnter={(_, i) => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} hide={!presentSeries.includes('High')} />
              <Bar isAnimationActive={false} minPointSize={2} dataKey="Weighted" fill={colors.Weighted} radius={[4, 4, 0, 0]} onMouseEnter={(_, i) => setActiveIndex(i)} onMouseLeave={() => setActiveIndex(null)} hide={!presentSeries.includes('Weighted')} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="flex flex-wrap justify-center gap-6 mt-6">
          {Object.entries(colors).map(([key, color]) => (
            <div key={key} className="flex items-center gap-2">
              <div className="w-4 h-4 rounded" style={{ backgroundColor: color }} />
              <span className="text-sm text-succession-text-secondary">{seriesLabels[key] || key}</span>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );
}


